import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Card from "../../../componentLibrary/Card";
import Input from "../../../componentLibrary/Input";
import Spinner from "../../../componentLibrary/Spinner";
import { Role } from "../../../types/rbac";

interface RoleListProps {
  roles: Role[];
  selectedRole: Role | null;
  search: string;
  isLoading: boolean;
  error: Error | null;
  onSearchChange: (value: string) => void;
  onRoleSelect: (role: Role) => void;
  onEditRole: (role: Role) => void;
  onDeleteRole: (role: Role) => void;
}

const RoleList = ({
  roles,
  selectedRole,
  search,
  isLoading,
  error,
  onSearchChange,
  onRoleSelect,
  onEditRole,
  onDeleteRole,
}: RoleListProps) => {
  return (
    <Card title="Roles">
      <div className="mb-4">
        <Input
          type="text"
          placeholder="Search roles..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {error && (
        <div className="text-red-600 py-4">
          Error loading roles: {error.message}
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {roles.map((role) => (
            <div
              key={role._id}
              className={`p-3 border rounded-lg cursor-pointer hover:border-electrum-300 transition-colors ${
                selectedRole?._id === role._id
                  ? "border-electrum-500 bg-electrum-50"
                  : "border-gray-200"
              }`}
              onClick={() => onRoleSelect(role)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-semibold">{role.displayName}</div>
                  <div className="text-sm text-gray-500">{role.name}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {role.permissions.length} permission(s)
                  </div>
                </div>
                {role.name !== "owner" && (
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditRole(role);
                      }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <PencilIcon className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteRole(role);
                      }}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <TrashIcon className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {roles.length === 0 && (
            <div className="text-center py-8 text-gray-500">No roles found</div>
          )}
        </div>
      )}
    </Card>
  );
};

export default RoleList;
